import { useEffect, useState } from "react";
import { List } from "lucide-react";

type Heading = { id: string; text: string; level: 2 | 3 };

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

/**
 * Sticky in-page table of contents. Scans the nearest `.prose-article`
 * container for h2/h3 headings, assigns stable ids, and highlights the
 * section currently in view via IntersectionObserver.
 */
export function ArticleTableOfContents({ contentSelector = ".prose-article" }: { contentSelector?: string }) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const root = document.querySelector(contentSelector);
    if (!root) return;

    const nodes = Array.from(root.querySelectorAll("h2, h3")) as HTMLHeadingElement[];
    const used = new Set<string>();
    const collected: Heading[] = nodes.map((node) => {
      const text = (node.textContent || "").trim();
      let id = node.id || slugify(text);
      if (!id) id = `section-${Math.random().toString(36).slice(2, 8)}`;
      let unique = id;
      let i = 2;
      while (used.has(unique)) unique = `${id}-${i++}`;
      used.add(unique);
      node.id = unique;
      // Offset for sticky header anchor jumps
      node.style.scrollMarginTop = "6rem";
      return {
        id: unique,
        text,
        level: node.tagName === "H2" ? 2 : 3,
      };
    });

    setHeadings(collected);

    if (collected.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId((visible[0].target as HTMLElement).id);
        }
      },
      { rootMargin: "-96px 0px -65% 0px", threshold: 0 }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [contentSelector]);

  if (headings.length < 3) return null;

  return (
    <nav aria-label="Table of contents" className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <List className="h-4 w-4 text-brand-gold-deep" aria-hidden="true" />
        <span className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
          On this page
        </span>
      </div>
      <ul className="mt-3 max-h-[60vh] space-y-1 overflow-y-auto text-sm">
        {headings.map((h) => {
          const isActive = h.id === activeId;
          return (
            <li key={h.id} className={h.level === 3 ? "ml-3" : ""}>
              <a
                href={`#${h.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(h.id);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    history.replaceState(null, "", `#${h.id}`);
                  }
                }}
                className={`block rounded-md border-l-2 py-1.5 pl-3 pr-2 leading-snug transition-smooth ${
                  isActive
                    ? "border-brand-gold bg-brand-gold/10 font-bold text-foreground"
                    : "border-transparent text-foreground/70 hover:border-brand-gold/40 hover:bg-muted hover:text-foreground"
                } ${h.level === 3 ? "text-[0.8rem]" : ""}`}
              >
                {h.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
